import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button, FormControl, Input, InputLabel, Typography, Link, Chip } from '@material-ui/core';
import InputMask from 'react-input-mask';
import {ReactComponent as Success} from '../assets/green-confirm-check.svg';
import parsePhoneNumber from 'libphonenumber-js'
import {ReactComponent as AddContact} from '../assets/add-contact.svg';
import { __ } from '../lib/localization';

const CallRequestForm = (props) => {
    const [isProcessingForm, setIsProcessingForm] = useState(false);
    const [isFormSent, setIsFormSent] = useState(false);
    const [errors, setErrors] = useState({});
    const [phone, setPhone] = useState("");
    const [name, setName] = useState("");
    const [times, setTimes] = useState([]);
    const [message, setMessage] = useState("");
    const formWrapperRef = useRef(null);
    const [isFormStarted, setIsFormStarted] = useState(false);
    const [formStep, setFormStep] = useState(1);

    const callRequestFormRef = useRef(null);

    useEffect(() => {
        // if on step 2, scroll the form view down to reveal the next info
        if (formStep === 2) {
            callRequestFormRef.current.scrollBy({ top: 10000, behavior: "smooth" });
        }
    }, [formStep]);

    const getPhoneNumber = () => {
        return parsePhoneNumber(props.callFromNumber ?? '+12133734253')?.formatNational(); // Failsafe just in case no cellphone is returned
    }

    const getAddToContactsBlob = () => {
        const vcardData = ['BEGIN:VCARD', 'VERSION:3.0'];

        if (props.vcardData.org) {
            vcardData.push('ORG:' + props.vcardData.org);
        }
        if (props.vcardData.message_number) {
            vcardData.push('TEL;TYPE=MSG:' + props.vcardData.message_number);
        }
        if (props.vcardData.voice_number) {
            vcardData.push('TEL;TYPE=VOICE:' + props.vcardData.voice_number);
        }
        if (props.vcardData.url) {
            vcardData.push('URL:' + props.vcardData.url);
        }
        if (props.vcardData.adr) {
            vcardData.push('ADR:' + props.vcardData.adr);
        }
        if (props.vcardData.logo) {
            let logo = props.vcardData.logo;
            const type = logo.startsWith('data:image/png')
                ? 'PNG'
                : 'JPEG';
            vcardData.push('PHOTO;ENCODING=b;TYPE='+type+':' + props.vcardData.logo.replace(/^.*base64,/, ''));
        }

        vcardData.push('END:VCARD');

        var file = new Blob([vcardData.join("\n")], {type: 'text/x-vcard'});

        return URL.createObjectURL(file);
    }

    const getAddToContactsFilename = () => {
        let name = 'vcard';

        if (props.vcardData.org) {
            name = (props.vcardData.org)
                .toLowerCase()
                .replace(/\W/, '-')
                .replace(/[-]+/, '-')
                .replace(/(^[-]+|[-]+$)/, '')
        }

        return name + '.vcf';
    }

    const getSubmitLabel = () => {
        switch (formStep) {
            case 1: return __('leadbox_submit');
            case 2: return __('leadbox_send');
            default: return __('leadbox_close');
        }
    }

    const isSubmitDisabled = () => {
        if (isProcessingForm) {
            return true;
        }

        if (formStep === 1) {
            return name === "" || phone == "" || times.length === 0;
        }
        if (formStep === 2) {
            return message === "";
        }

        return false;
    }

    const onFieldFocus = (inputName) => {
        props.Analytics.trackEvent('form-field', inputName)
        if (!isFormStarted) {
            props.Analytics.trackEvent('form-field', 'form_start_call_request')
            setIsFormStarted(true);
        }
    }

    const handleSubmission = () => {
        if (formStep === 1) {
            return validate();
        }
        if (formStep === 2) {
            return attachMessage();
        }
        else {
            props.handleCloseLeadbox();
        }
    }

    const validate = () => {
        setIsProcessingForm(true);
        props.Analytics.trackEvent('buttons', 'call-request-form-1-submit-cta');

        const isPhone = RegExp(/^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/) // us

        let error = {}
        let valid = true;
        if (phone === "") {
            error.phone = __('leadbox_required');
            props.Analytics.trackEvent('form-field-error', 'call-request-phone');
            valid = false;
        } else {
            const cleanPhone = phone.replace("+1", "").replace(" ", "");
            const isValid = isPhone.test(cleanPhone);
            if (!isValid) {
                error.phone = __('leadbox_error_number');
                props.Analytics.trackEvent('form-field-error', 'call-request-phone');
                valid = false
            }
        }
        if (name === "") {
            error.name = __('leadbox_required');
            valid = false;
        } else if (name.length < 3) {
            error.name = __('leadbox_error_name');
            props.Analytics.trackEvent('form-field-error', 'call-request-name');
            valid = false;
        }

        if (valid) {
            const cleanPhone = phone
                .replace(/\-/g, "")
                .replace(/\s/g, "")
                .replace("(", "")
                .replace(")", "");
            const obj = JSON.stringify({
                name: name,
                cellphone: cleanPhone,
                message: message.substring(0, 3000),
                source: props.Analytics.getHref() || "",
                type: "call_request_note",
                preferred_times: times,
            });
            axios.post(props.endpoint, obj, {
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    }
                }
            ).then((e) => {
                if (e.status === 204) {
                    setErrors({});
                    props.Analytics.trackEvent('leadbox', 'call-request-form-1-submitted');
                    setFormStep(2); // advance the "wizard"
                    props.setHeaderTitle(__('call_request_submitted_teaser'));
                    setTimeout(() => {
                        formWrapperRef?.current?.focus(); // we focus the content wrapper to set the tab position
                        formWrapperRef?.current?.blur(); // and then we blur so as to not show the :focus styling around the div. The tab position should still be where we want it.
                    }, 125);
                    //return setIsFormSent(true)
                }
            }).catch((err) => {
                switch (err.response.data.errors[0].errorCode) {
                    case 'invalidPhoneNumber':
                        error.phone = __('leadbox_error_number');
                        break;
                    case 'maxLength.openapi.validation':
                        error.name = __('leadbox_error_msg');
                        break;
                    default:
                        error.api = err.response.data.message;

                }
                valid = false;
                return setErrors(error);
            })
            .finally(e => setIsProcessingForm(false));
        } else {
            setIsProcessingForm(false);
            return setErrors(error);
        }
    };

    const attachMessage = () => {
        setIsProcessingForm(true);
        props.Analytics.trackEvent('buttons', 'call-request-form-2-submit-cta');

        let error = {}
        let valid = true;
        if (message === "") {
            error.name = __('leadbox_required');
            valid = false;
        }

        if (valid) {
            const obj = JSON.stringify({
                name: name,
                cellphone: phone.replace(/\-/g, "").replace(/\s/g, "").replace("(", "").replace(")", ""),
                message: message.substring(0, 3000),
                source: props.Analytics.getHref() || "",
                type: "call_request_message",
            });
            axios.post(props.endpoint, obj, {
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    }
                }
            ).then((e) => {
                if (e.status === 204) {
                    setErrors({});
                    props.Analytics.trackEvent('leadbox', 'call-request-form-2-submitted');
                    setFormStep(3); // advance the "wizard"
                    props.setHeaderTitle(__('call_request_received_teaser'));
                    setTimeout(() => {
                        formWrapperRef?.current?.focus(); // we focus the content wrapper to set the tab position
                        formWrapperRef?.current?.blur(); // and then we blur so as to not show the :focus styling around the div. The tab position should still be where we want it.
                    }, 125);
                    return setIsFormSent(true)
                }
            }).catch((err) => {
                switch (err.response.data.errors[0].errorCode) {
                    case 'invalidPhoneNumber':
                        error.phone = __('leadbox_error_number');
                        break;
                    case 'maxLength.openapi.validation':
                        error.name = __('leadbox_error_msg');
                        break;
                    default:
                        error.api = err.response.data.message;

                }
                valid = false;
                return setErrors(error);
            })
            .finally(e => setIsProcessingForm(false));
        } else {
            setIsProcessingForm(false);
            return setErrors(error);
        }
    };

    const getTimeChipStyle = (value) => {
        return times.indexOf(value) === -1
            ? {backgroundColor: 'transparent', color: '#36393D', borderColor: props.accentColor}
            : {backgroundColor: props.accentColor, borderColor: props.accentColor, color: '#ffffff'};
    }

    const timeChips = (props.metadata?.availability || []).map((item, index) => {
        return <Chip
            label={item}
            classes={{
                root: props.classes.callRequestTimeButton,
                label: `${props.classes.callRequestTimeButton__label} ${item === 'asap' ? props.classes.isUppercase : ''}`,
            }}
            //style={{...{backgroundColor: props.accentColor, border: "2px solid red"}}}
            style={getTimeChipStyle(item)}
            onClick={(e) => {
                toggleTime(item);
                //props.Analytics.trackEvent('buttons', 'add-to-contacts-cta')
            }}
            disabled={formStep !== 1}
        />
    });

    const toggleTime = (value) => {
        if (times.indexOf(value) > -1) {
            setTimes(times.filter(i => { return i !== value; }));
        }
        else {
            setTimes([...times, value]);
        }
    }

    return (
        <div ref={formWrapperRef} tabindex="-1">
            {formStep === 1 || formStep === 2 ? (
                <form
                    id="leadbox-call-request-form"
                    ref={callRequestFormRef}
                    name="leadbox-call-request-form"
                    autoComplete="off"
                    className={props.classes.call_request_form__body}
                >
                    <div style={formStep === 2 ? {height: '100%', marginBottom: '80px'} : {}} id="call-request-step-1">
                        <FormControl fullWidth>
                            <InputLabel
                                className={props.classes.root}
                                htmlFor="name"
                                error={errors.name !== undefined}
                            >
                                {__('leadbox_name')} <span>({errors.name})</span>
                            </InputLabel>
                            <Input
                                className={props.classes.root}
                                required
                                id="name"
                                value={name.slice(0,69)}
                                onFocus={() => { onFieldFocus('call-request-name') }}
                                onChange={(e) => { setName(e.target.value) }}
                                disabled={formStep > 1}
                            />
                        </FormControl>

                        <div>
                            <InputMask
                                mask="+1 (999) 999-9999"
                                value={phone}
                                disabled={formStep > 1}
                                maskChar=" "
                                onChange={(e) => {
                                    (errors.phone !== undefined) && setErrors(prevState => ({
                                        ...prevState,
                                        phone: undefined
                                    }));
                                    setPhone(e.target.value)
                                }}
                            >
                                {() => <FormControl fullWidth>
                                    <InputLabel
                                        className={props.classes.root} htmlFor="phone"
                                        error={errors.phone !== undefined}
                                    >
                                        {__('leadbox_number')}
                                        <span>({errors.phone})</span>
                                    </InputLabel>
                                    <Input
                                        className={props.classes.root}
                                        required
                                        id="phone"
                                        onFocus={() => { onFieldFocus('call-request-phone') }}
                                        disabled={formStep > 1}
                                    />
                                </FormControl>}
                            </InputMask>
                        </div>

                        <div style={{marginTop: '15px', position: 'relative', paddingTop: "22px"}}>
                            <p className={`MuiFormLabel-root MuiInputLabel-root MuiInputLabel-formControl ${props.classes.root}`} style={{transform: "translate(0, 5px)"}}>{__('call_request_select_preferred_times')}</p>
                            {timeChips}
                        </div>
                    </div>
                    {formStep === 2 ? (
                        <div style={{height: 'calc(100% - 17px)'}} id="call-request-step-2">
                            <div style={{marginTop: '15px', position: 'relative', paddingTop: "22px"}}>
                                <p
                                    className={`MuiFormLabel-root MuiInputLabel-root MuiInputLabel-formControl ${props.classes.root}`}
                                    style={{transform: "translate(0, 5px)", color: "#ccc", fontSize: '14px'}}
                                >
                                    {__('call_request_selected_times')}
                                </p>
                                {timeChips.filter(i => { return times.indexOf(i.props.label) > -1; })}
                            </div>
                            <hr noshade="true" style={{height: "1px", border: 0, margin: "2px 0 5px 0", background: "#F2F3F4"}}></hr>
                            <div>
                                <FormControl fullWidth>
                                    <InputLabel className={props.classes.root} htmlFor="message">{__('call_request_reason_for_call')}</InputLabel>
                                    <Input
                                        multiline
                                        rows={3}
                                        className={props.classes.textArea}
                                        required
                                        id="message"
                                        value={message}
                                        onFocus={() => { onFieldFocus('call-request-message') }}
                                        onChange={(e) => {
                                            setMessage(e.target.value)
                                    }}/>
                                </FormControl>
                            </div>
                        </div>
                    ) : '' }
                </form>
            ) : (
                <div className={props.classes.form__body}>
                    {!props.vcardData?.org ? (
                        <Success style={{marginTop: '15px'}} aria-hidden="true" focusable="false" />
                     ) : (<></>)}
                    <Typography className={props.classes.success__text} style={{marginTop: (props.vcardData?.org ? '15px' : '')}}>
                        <strong style={{textTransform: 'uppercase'}}>{ __('leadbox_thank_you') }</strong>

                        <p style={{margin: "0 0 12px 0"}}>{ __('call_request_received') }</p>

                        {props.vcardData?.org ? (
                         <React.Fragment>
                             <Button
                                 href={getAddToContactsBlob()}
                                 classes={{
                                     root: props.classes.addToContactsButton,
                                     label: props.classes.addToContactsButton__label,
                                 }}
                                 style={{backgroundColor: "#39AF25", margin: "5px auto"}}
                                 onClick={ () => { props.Analytics.trackEvent('buttons', 'add-to-contacts-cta') }}
                                 download={getAddToContactsFilename()}
                                 startIcon=<AddContact aria-hidden="true" focusable="false" />
                             >
                                 { __('leadbox_cta_vcard') }
                             </Button>
                         </React.Fragment>
                         ) : (<></>)}
                        <p style={{ margin: "16px 0 0"}}>
                            { __('call_request_confirmation') } <br/><strong>{getPhoneNumber()}</strong>
                        </p>
                    </Typography>
                </div>
            )}

            <div className={props.classes.form__triangle}></div>
            <div>
              {!isFormSent ? (
                <div>
                 <Typography className={props.classes.submit__note}>
                     {__(
                         'leadbox_submit_terms',
                         { Business_name: props.vcardData?.org || "this business" }
                      )}
                    </Typography>
                 <Typography className={props.classes.error}>{errors?.api}</Typography>
              </div>
              ) : (<></>)}
              <Button
                classes={{
                  root: props.classes.sendbutton,
                  label: props.classes.sendbutton__label,
                  disabled: props.classes.sendbutton__disabled
                }}
                disabled={isSubmitDisabled()}
                style={{...{backgroundColor: props.accentColor}, ...(isFormSent ? {marginTop: "14px", marginBottom: "14px"} : {})}}
                onClick={handleSubmission}
                aria-label={getSubmitLabel()}
              >{getSubmitLabel()}</Button>
            </div>
        </div>
    )
}

export default CallRequestForm;
